import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import {
    useTimesheetsById,
    useUpdateTimesheetMutation,
} from "presentation/hooks/use-query-timesheet";
import {
    useTimesheetDaysByFilter,
    useCreateAllTimesheetDayMutation,
    useUpdateTimesheetDayMutation,
    useDeleteTimesheetDayMutation,
} from "presentation/hooks/use-query-timesheet-day";
import { TimesheetDayInputFilter } from "domain/models/timesheet-day-input-filter";
import { TimesheetDay } from "domain/entities/timesheet-day";
import { Timesheet } from "domain/entities/timesheet";
import Spinner from "presentation/components/Shared/Spinner";
import WeekDayCard from "./components/WeekDayCard";
import MultiDayEntryModal from "../MultiDayEntryModal";
import UploadAbsenceDocumentModal from "../UploadAbsenceDocumentModal";
import SubmitTimesheetModal from "../SubmitTimesheetModal";
import { useDocumentByFilter } from "presentation/hooks/use-query-document";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { useAbscenceTypesByFilter } from "presentation/hooks/use-query-abscence-type";
import { AbscenceTypeInputFilter } from "domain/models/abscence-type-input-filter";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useTripsheetsByFilter } from "presentation/hooks/use-query-tripsheet";
import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";
import { usePurchaseOrdersByFilter } from "presentation/hooks/use-query-purchase-order";
import { PurchaseOrderInputFilter } from "domain/models/purchase-order-input-filter";
import { EMAIL } from "domain/enum/email";
import { validateTimesheet } from "../../utils/validate-timesheet.util";
import MobileS3PDFViewerDialog from "presentation/mobile/components/MobileS3PDFViewerDialog";
import MobileImageViewerDialog from "presentation/mobile/components/MobileImageViewerDialog";
import { MONTHS_PT, calculateWeeksOfMonth, getInitialWeekIndex, } from "../../utils/calendar.util";
import { useTimesheetPdfGenerator } from "../../hooks/useTimesheetPdfGenerator";
import WeeklyHeader from "./components/WeeklyHeader";
import { Document } from "domain/entities/document";
import MobileLoadingOverlay from "presentation/mobile/components/MobileLoadingOverlay";
import MobileToast from "presentation/mobile/components/MobileToast";
import { isNoHoursPoType } from "presentation/mobile/utils/po-types.util";

const WeeklyView = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const navigationState = useNavigationStore();
    const toast = useRef<Toast>(null);

    // Estado para controlar qual card de dia está expandido
    const [expandedDay, setExpandedDay] = useState<number | null>(null);
    const updateTimesheetDay = useUpdateTimesheetDayMutation();
    const deleteTimesheetDay = useDeleteTimesheetDayMutation();

    const [weekIndex, setWeekIndex] = useState(0);
    const [localTimesheetDays, setLocalTimesheetDays] = useState<TimesheetDay[]>([]);
    const [showMultiDayModal, setShowMultiDayModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);

    const hasInitializedWeek = useRef(false);

    const { userEmployee, email: userEmployeeEmail } = useAuthStore();
    const updateTimesheetMutation = useUpdateTimesheetMutation();

    // Query das POs do colaborador para identificar Horas Extras
    const { data: purchaseOrdersData } = usePurchaseOrdersByFilter({
        limit: 100,
        employeeId: userEmployee?.id,
    } as PurchaseOrderInputFilter);

    const extraHourPoIds = useMemo(() => {
        const set = new Set<string>();
        purchaseOrdersData?.data?.forEach((po) => {
            if (po.type?.toLowerCase() === "hora extra" && po.id) {
                set.add(po.id);
            }
        });
        return set;
    }, [purchaseOrdersData]);

    // POs de Deslocação/Prevenção (gravadas com 0h) para continuarem visíveis no dia
    const noHoursPoIds = useMemo(() => {
        const set = new Set<string>();
        purchaseOrdersData?.data?.forEach((po) => {
            if (isNoHoursPoType(po.type) && po.id) {
                set.add(po.id);
            }
        });
        return set;
    }, [purchaseOrdersData]);

    // Geração do relatório PDF em background desacoplada num Hook limpo
    const { uploadPdfInBackground } = useTimesheetPdfGenerator(id, userEmployee);

    // Consulta se existem viagens/KM pendentes de assinatura
    const { data: tripsheetsData } = useTripsheetsByFilter({
        employeeId: userEmployee?.id,
        status: "Assinado 1",
    } as TripsheetInputFilter);

    const hasPendingKm = (tripsheetsData?.data?.length ?? 0) > 0;

    const ABSENCE_DOCUMENT_TYPE_ID = "a87b3dc1-45f1-4389-bc9a-f65c356de235";

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedDayForUpload, setSelectedDayForUpload] = useState<TimesheetDay | null>(null);

    const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);

    // Query: AbscenceTypes (para saber documentRequired)
    const { data: abscenceTypesData } = useAbscenceTypesByFilter({
        limit: 100,
        contractType: userEmployee?.contractType,
    } as AbscenceTypeInputFilter);

    // Query idêntica ao Desktop: busca todos os documentos associados a esta timesheet
    const { data: documentsData, refetch: refetchDocuments } = useDocumentByFilter(
        {
            limit: 100,
            timesheetId: id,
        } as DocumentInputFilter,
        { enabled: !!id }
    );

    // Map: timesheetDayId → Document (para conseguirmos aceder ao ficheiro do dia)
    const documentByDayId = useMemo(() => {
        const map = new Map<string, Document>();
        documentsData?.data?.forEach((doc) => {
            const tsdId = (doc.timesheetDay as any)?.id;
            if (tsdId) map.set(tsdId, doc);
        });
        return map;
    }, [documentsData]);

    // Set de IDs dos dias que já têm atestado anexado
    const daysWithDocument = useMemo(() => {
        return new Set(documentByDayId.keys());
    }, [documentByDayId]);

    // Map: abscenceTypeId → documentRequired
    const documentRequiredByAbscenceType = useMemo(() => {
        const map = new Map<string, boolean>();
        abscenceTypesData?.data?.forEach((ab) => {
            map.set(ab.id ?? "", ab.documentRequired ?? false);
        });
        return map;
    }, [abscenceTypesData]);

    const handleDocumentClick = (day: TimesheetDay) => {
        setSelectedDayForUpload(day);
        setShowUploadModal(true);
    };

    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isBulkSaving, setIsBulkSaving] = useState<boolean>(false);

    // Optimistic UI: aplicar mudanças localmente, enviar em background, reverter se falhar
    const handleBulkSave = async (payload: TimesheetDay[]) => {
        if (!timesheet?.id || isBulkSaving) return;
        setIsBulkSaving(true);

        const previousDays = [...localTimesheetDays];
        const optimisticDays = [...localTimesheetDays];
        payload.forEach((incoming) => {
            const idx = optimisticDays.findIndex((d) => d.day === incoming.day);
            if (idx >= 0) {
                optimisticDays[idx] = { ...optimisticDays[idx], ...incoming };
            } else {
                optimisticDays.push(incoming);
            }
        });
        setLocalTimesheetDays(optimisticDays);

        try {
            // O POST /timesheetDay/bulk ignora dias que já existem (backend: _checkForDuplicate).
            // Tal como a web: dias novos vão em bulk, dias existentes são atualizados com PUT.
            const daysToCreate = payload.filter((d) => !d.id);
            const daysToUpdate = payload.filter((d) => !!d.id);

            if (daysToCreate.length > 0) {
                await createAllTimesheetDays(daysToCreate);
            }
            // Sequencial: o backend recalcula o plafond da PO em cada atualização
            for (const dayToUpdate of daysToUpdate) {
                await updateTimesheetDay(dayToUpdate);
            }

            if (timesheet && timesheet.status === "Novo") {
                await updateTimesheetMutation.mutateAsync({
                    ...timesheet,
                    status: "Editado",
                    lastModifiedBy: userEmployeeEmail ?? EMAIL,
                });
                await refetchTimesheet();
            }

            await refetchDays();

            // Só fecha quando todo o fluxo terminou (gravação + estado + dados atualizados)
            setShowMultiDayModal(false);
            toast.current?.show({
                severity: "success",
                summary: "Guardado",
                detail: `${payload.length} dia(s) atualizado(s).`,
                life: 2500,
            });
        } catch (error: any) {
            setLocalTimesheetDays(previousDays);
            // Se parte dos dias já foi gravada antes do erro, sincroniza com o servidor
            await refetchDays();

            const errorMessage =
                error?.response?.data?.metadata?.errors?.[0] ||
                error?.response?.data?.errors?.[0] ||
                error?.message ||
                "Ocorreu um erro ao guardar.";

            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: errorMessage,
                life: 5000,
            });
        } finally {
            setIsBulkSaving(false);
        }
    };

    const handleUploadSuccess = async () => {
        await refetchDocuments();
    };

    const {
        data: timesheetData,
        isLoading: isTimesheetLoading,
        error: timesheetError,
        refetch: refetchTimesheet,
    } = useTimesheetsById(id);

    const {
        data: timesheetDaysData,
        isLoading: isDaysLoading,
        refetch: refetchDays,
    } = useTimesheetDaysByFilter(
        { timesheet: id ?? "", limit: 100, sortBy: "day", sortOrder: "asc" } as TimesheetDayInputFilter,
        !!id
    );

    // Hook para bulk create/update
    const createAllTimesheetDays = useCreateAllTimesheetDayMutation();

    useEffect(() => {
        navigationState.setCurrentRoute("timesheets");
    }, []);

    // Sincronizar dados remotos com estado local
    useEffect(() => {
        if (!timesheetDaysData?.data || isDaysLoading) return;
        setLocalTimesheetDays(timesheetDaysData.data);
    }, [timesheetDaysData?.data, isDaysLoading]);

    const timesheet = timesheetData?.data;

    // Estados editáveis pelo colaborador
    const isEditableStatus = timesheet
        ? ["Novo", "Editado", "Rejeitado"].includes(timesheet.status)
        : false;
    const canEdit = isEditableStatus && !hasPendingKm;

    const daysByNumber = useMemo(() => {
        const map = new Map<number, TimesheetDay>();
        localTimesheetDays.forEach((d) => map.set(d.day, d));
        return map;
    }, [localTimesheetDays]);

    // Gera a matriz das semanas usando o utilitário
    const weeks = useMemo(() => {
        if (!timesheet) return [];
        return calculateWeeksOfMonth(timesheet.year, timesheet.month);
    }, [timesheet?.year, timesheet?.month]);

    // Posiciona automaticamente na semana do dia atual
    useEffect(() => {
        if (!timesheet || weeks.length === 0 || hasInitializedWeek.current) return;
        setWeekIndex(getInitialWeekIndex(timesheet.year, timesheet.month, weeks));
        hasInitializedWeek.current = true;
    }, [timesheet, weeks]);

    const currentWeek = weeks[weekIndex] ?? [];

    const handlePrev = () => setWeekIndex((prev) => Math.max(0, prev - 1));
    const handleNext = () =>
        setWeekIndex((prev) => Math.min(weeks.length - 1, prev + 1));

    // Função unificada de eliminação de entradas (PO ou Ausência)
    const handleDeleteEntry = (
        tsd: TimesheetDay,
        type: "po" | "abscence",
        poId?: string
    ) => {
        const isPo = type === "po";
        confirmDialog({
            message: `Tem a certeza que deseja remover ${isPo ? "este registo de projeto" : "esta ausência"}?`,
            header: isPo ? "Remover Entrada" : "Remover Ausência",
            icon: "pi pi-trash",
            acceptLabel: "Sim, Remover",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: async () => {
                setIsDeleting(true);
                try {
                    const remainingPos = isPo
                        ? (tsd.purchaseOrders || []).filter((p) => p.id !== poId)
                        : (tsd.purchaseOrders || []);
                    const remainingAbscence = isPo ? tsd.abscenceType : undefined;
                    const remainingAbscenceTime = isPo ? tsd.abscenceTime : 0;
                    const totalRemaining = remainingPos.length + (remainingAbscence && (remainingAbscenceTime ?? 0) > 0 ? 1 : 0);

                    if (totalRemaining > 0) {
                        const updated: TimesheetDay = {
                            ...tsd,
                            purchaseOrders: remainingPos,
                            abscenceType: remainingAbscence
                                ? { ...remainingAbscence, hours: remainingAbscenceTime ?? 0 }
                                : remainingAbscence,
                            abscenceTime: remainingAbscenceTime,
                            lastModifiedBy: userEmployeeEmail ?? EMAIL,
                        };
                        await updateTimesheetDay(updated);
                        setLocalTimesheetDays((prev) =>
                            prev.map((d) => (d.day === tsd.day ? updated : d))
                        );
                    } else if (tsd.id) {
                        await deleteTimesheetDay(tsd);
                        setLocalTimesheetDays((prev) => prev.filter((d) => d.day !== tsd.day));
                        setExpandedDay(null);
                    }

                    if (timesheet && timesheet.status === "Novo") {
                        await updateTimesheetMutation.mutateAsync({
                            ...timesheet,
                            status: "Editado",
                            lastModifiedBy: userEmployeeEmail ?? EMAIL,
                        });
                        await refetchTimesheet();
                    }

                    toast.current?.show({
                        severity: "success",
                        summary: "Removido",
                        detail: `${isPo ? "Entrada" : "Ausência"} removida com sucesso.`,
                        life: 2500,
                    });
                    await refetchDays();
                } catch (error) {
                    toast.current?.show({
                        severity: "error",
                        summary: "Erro",
                        detail: "Ocorreu um erro ao remover.",
                        life: 3000,
                    });
                } finally {
                    setIsDeleting(false);
                }
            },
        });
    };

    const handleOpenSubmitDialog = () => {
        if (!timesheet) return;

        const isValid = validateTimesheet({
            timesheet,
            localTimesheetDays,
            daysByNumber,
            userEmployee,
            extraHourPoIds,
            toastRef: toast,
        });

        if (!isValid) return;
        setShowSubmitModal(true);
    };

    const handleSubmitTimesheet = async () => {
        if (!timesheet) return;
        setIsSubmitting(true);

        const updatedTimesheet: Timesheet = {
            ...timesheet,
            lastModifiedBy: userEmployeeEmail ?? EMAIL,
            status: "Enviado",
        };

        try {
            await updateTimesheetMutation.mutateAsync(updatedTimesheet);

            toast.current?.show({
                severity: "success",
                summary: "Submetido",
                detail: "Mapa de horas enviado com sucesso.",
                life: 3000,
            });

            uploadPdfInBackground();

            setTimeout(() => {
                navigate("/mobile/timesheets");
            }, 1500);
        } catch (error: any) {
            const message =
                error?.response?.data?.metadata?.errors?.[0] ||
                error?.response?.data?.errors?.[0] ||
                "Ocorreu um erro ao submeter o mapa de horas.";

            toast.current?.show({
                severity: "error",
                summary: "Erro ao Submeter",
                detail: message,
                life: 5000,
            });
            // Only unlock on error: on success the page navigates away after 1.5s
            setIsSubmitting(false);
        }
    };

    if (isTimesheetLoading || isDaysLoading) {
        return (
            <div className="flex justify-content-center align-items-center h-full pt-6">
                <Spinner />
            </div>
        );
    }

    if (timesheetError || !timesheet) {
        return (
            <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                <i className="pi pi-exclamation-triangle text-4xl" style={{ color: "#B00020" }} />
                <span className="text-sm" style={{ color: "#888" }}>
                    Timesheet não encontrado.
                </span>
                <Button
                    label="Voltar"
                    rounded
                    onClick={() => navigate("/mobile/timesheets")}
                />
            </div>
        );
    }

    const periodLabel = `${MONTHS_PT[timesheet.month - 1]}/${timesheet.year}`;

    return (
        <div className="flex flex-column h-full">
            {/* Cabeçalho e Navegação de Semanas Modular */}
            <WeeklyHeader
                periodLabel={periodLabel}
                status={timesheet.status}
                isEditableStatus={isEditableStatus}
                canEdit={canEdit}
                hasPendingKm={hasPendingKm}
                onSubmitClick={handleOpenSubmitDialog}
                weekIndex={weekIndex}
                totalWeeks={weeks.length}
                onPrevWeek={handlePrev}
                onNextWeek={handleNext}
            />

            {/* Lista dos Dias da Semana */}
            <div className="flex-grow-1 overflow-auto px-3 py-3 flex flex-column gap-2">
                {currentWeek.map((day, idx) => {
                    if (day === 0) {
                        return null;
                    }

                    const tsd = daysByNumber.get(day);
                    const abscenceTypeId = tsd?.abscenceType?.id;

                    const isWeekendCol = idx === 5 || idx === 6;
                    const hasRecords =
                        (tsd?.purchaseOrders?.length ?? 0) > 0 || (tsd?.abscenceTime ?? 0) > 0;
                    const canFillWeekends = userEmployee?.isSuportService || extraHourPoIds.size > 0;

                    if (isWeekendCol && !hasRecords && !canFillWeekends) {
                        return null;
                    }

                    return (
                        <WeekDayCard
                            key={`${weekIndex}-${idx}`}
                            day={day}
                            month={timesheet.month}
                            year={timesheet.year}
                            timesheetDay={tsd}
                            extraHourPoIds={extraHourPoIds}
                            noHoursPoIds={noHoursPoIds}
                            documentRequired={
                                abscenceTypeId
                                    ? documentRequiredByAbscenceType.get(abscenceTypeId) ?? false
                                    : false
                            }
                            hasDocument={tsd?.id ? daysWithDocument.has(tsd.id) : false}
                            onDocumentClick={handleDocumentClick}
                            isExpanded={expandedDay === day}
                            onToggleExpand={() =>
                                setExpandedDay((prev) => (prev === day ? null : day))
                            }
                            onDeletePo={(dayItem, poId) => handleDeleteEntry(dayItem, "po", poId)}
                            onDeleteAbscence={(dayItem) => handleDeleteEntry(dayItem, "abscence")}
                            canEdit={canEdit}
                            isSuportService={userEmployee?.isSuportService}
                        />
                    );
                })}
            </div>

            {/* Botão de adicionar só é visível se o mapa for editável */}
            {canEdit && (
                <Button
                    icon="pi pi-plus"
                    rounded
                    className="fixed"
                    onClick={() => setShowMultiDayModal(true)}
                    style={{ bottom: "6.5rem", right: "20px", zIndex: 1000 }}
                />
            )}

            {/* Modal Multi-Day */}
            <MultiDayEntryModal
                visible={showMultiDayModal}
                setVisible={setShowMultiDayModal}
                timesheet={timesheet}
                currentDays={localTimesheetDays}
                toastRef={toast}
                onSave={handleBulkSave}
            />

            {/* Modal Upload Atestado */}
            {selectedDayForUpload && (
                <UploadAbsenceDocumentModal
                    visible={showUploadModal}
                    setVisible={setShowUploadModal}
                    timesheetDay={selectedDayForUpload}
                    month={timesheet.month}
                    year={timesheet.year}
                    toastRef={toast}
                    existingDocument={selectedDayForUpload.id ? documentByDayId.get(selectedDayForUpload.id) : undefined}
                    onViewExisting={(fullPath) => setS3PathToDisplay(fullPath)}
                    onSuccess={handleUploadSuccess}
                />
            )}

            {/* Modal de Confirmação de Submissão Modular */}
            <SubmitTimesheetModal
                visible={showSubmitModal}
                onHide={() => !isSubmitting && setShowSubmitModal(false)}
                periodLabel={periodLabel}
                isSubmitting={isSubmitting}
                onConfirm={handleSubmitTimesheet}
            />


            {/* Visualizadores de Documentos (PDF ou Imagem) */}
            {s3PathToDisplay &&
                (s3PathToDisplay.toLowerCase().endsWith(".pdf") ? (
                    <MobileS3PDFViewerDialog
                        s3Path={s3PathToDisplay}
                        onHide={() => setS3PathToDisplay(null)}
                    />
                ) : (
                    <MobileImageViewerDialog
                        s3Path={s3PathToDisplay}
                        onHide={() => setS3PathToDisplay(null)}
                    />
                ))}

            {/* Overlay com Spinner durante a eliminação de registos */}
            <MobileLoadingOverlay visible={isDeleting} message="A remover registo..." />

            <MobileToast ref={toast} />
            <ConfirmDialog />
        </div>

    );
};

export default WeeklyView;